import * as yup from "yup";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import React from "react";
import { useFormik } from "formik";
import clsx from "clsx";
import moment from "moment";

import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import SelectInput from "components/common/InputField/SelectInput";
import TextareaInput from "components/common/InputField/TextareaInput";
import TextInput from "components/common/InputField/TextInput";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import VendorAvatar from "components/common/VendorAvatar";
import { useCreateSpaceTask } from "hooks/spaceHooks";
import { getTextFieldProps } from "helpers/getTextFieldProps";
import {
    SpaceTaskEndConditionEnum,
    SpaceTaskFrequencyEnum,
    SpaceTaskPriorityEnum,
    SpaceTaskStatusEnum,
    TaskScheduleTypeEnum,
    SpaceTaskReminderMethodEnum,
    Space,
    SpaceTaskAssignedToKindEnum,
} from "redux/Ldb/types/spaceTypes";
import Checkbox from "components/common/Checkbox";
import { normalizeMembers } from "utils/spaceUtils";
import { capitalizeFirstLetter } from "helpers/transformStrings";

type SpaceTaskAddModalProps = { spaceData: Space; status: SpaceTaskStatusEnum | string } & Omit<
    ModalContainerProps,
    "children"
>;

const SpaceTaskAddModal = (props: SpaceTaskAddModalProps) => {
    const { open, closeModal, ...restProps } = props;
    const { spaceData, status } = props;
    const spaceId = useParams().spaceId;

    const members = normalizeMembers(spaceData);

    const [createSpaceTaskMutation, createSpaceTaskMutationResult] = useCreateSpaceTask(spaceId);

    const initialValues = {
        name: "",
        description: "",
        status: status || SpaceTaskStatusEnum.todo,
        priority: SpaceTaskPriorityEnum.low,
        schedule: {
            type: TaskScheduleTypeEnum.oneTime,
            startAt: null,
            endAt: "",
            time: "00:00",
            recurrence: {
                frequency: SpaceTaskFrequencyEnum.daily,
                endCondition: SpaceTaskEndConditionEnum.none,
                endAt: "",
                endAfter: 0,
            },
        },
        assignees: [
            {
                kind: SpaceTaskAssignedToKindEnum.synth,
            },
        ],
        reminderMethods: [SpaceTaskReminderMethodEnum.email],
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: yup.object({
            name: yup.string().required("Name is required"),
            description: yup.string().required("Description is required"),
            status: yup.string().required("Status is required"),
            priority: yup.string().required("Priority is required"),
            schedule: yup.object({
                type: yup.string().required("Type is required"),
                startAt: yup.string().required("Start at is required"),
                endAt: yup.string().when("type", {
                    is: TaskScheduleTypeEnum.oneTime,
                    then: (schema) => schema.required("End at is required for one-time tasks"),
                    otherwise: (schema) => schema.optional(),
                }),
                time: yup.string().when("type", {
                    is: TaskScheduleTypeEnum.recurring,
                    then: (schema) => schema.required("Time is required"),
                    otherwise: (schema) => schema.optional(),
                }),
                recurrence: yup.object({
                    frequency: yup.string().when("type", {
                        is: TaskScheduleTypeEnum.recurring,
                        then: (schema) => schema.required("Frequency is required"),
                        otherwise: (schema) => schema.optional(),
                    }),
                    endCondition: yup.string().when("type", {
                        is: TaskScheduleTypeEnum.recurring,
                        then: (schema) => schema.required("End condition is required"),
                        otherwise: (schema) => schema.optional(),
                    }),
                    endAt: yup.string().when("endCondition", {
                        is: SpaceTaskEndConditionEnum.date,
                        then: (schema) => schema.required("End at is required"),
                        otherwise: (schema) => schema.optional(),
                    }),
                    endAfter: yup.string().when("endCondition", {
                        is: SpaceTaskEndConditionEnum.occurence,
                        then: (schema) => schema.required("End after is required"),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                }),
            }),
            assignees: yup
                .array()
                .of(
                    yup.object({
                        kind: yup.string().required("Kind is required"),
                        ldbId: yup.string(),
                        guestId: yup.string(),
                    })
                )
                .required("Assignees are required"),
            reminderMethods: yup.array().of(yup.string().required("Reminder method is required")),
        }),
        onSubmit: async (values) => {
            try {
                await createSpaceTaskMutation({
                    name: values?.name,
                    description: values?.description,
                    status: values?.status as SpaceTaskStatusEnum,
                    priority: values?.priority,
                    schedule: {
                        type: values?.schedule?.type,
                        startAt:
                            values?.schedule?.type === TaskScheduleTypeEnum.oneTime
                                ? values?.schedule?.startAt
                                : mergeDateTime(values?.schedule?.startAt, values?.schedule?.time),
                        ...(values?.schedule?.type === TaskScheduleTypeEnum.oneTime && {
                            endAt: values?.schedule?.endAt
                                ? new Date(values?.schedule?.endAt)
                                : null,
                        }),
                        ...(values?.schedule?.type === TaskScheduleTypeEnum.recurring && {
                            recurrence: {
                                frequency: values?.schedule?.recurrence?.frequency,
                                endCondition: values?.schedule?.recurrence?.endCondition,
                                ...(values?.schedule?.recurrence?.endCondition ===
                                    SpaceTaskEndConditionEnum.date && {
                                    endAt: values?.schedule?.recurrence?.endAt
                                        ? new Date(values?.schedule?.recurrence?.endAt)
                                        : null,
                                }),
                                ...(values?.schedule?.recurrence?.endCondition ===
                                    SpaceTaskEndConditionEnum.occurence && {
                                    endAfter: values?.schedule?.recurrence?.endAfter,
                                }),
                            },
                        }),
                    },
                    assignees: values?.assignees,
                    reminderMethods: values?.reminderMethods,
                });

                toast.custom((t) => (
                    <CustomToast t={t} message={`${"Task created successfully"}`} type="success" />
                ));
                handleAddEditSpaceCloseModal();
            } catch (error) {
                toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
            }
        },
    });

    const handleAddEditSpaceCloseModal = () => {
        closeModal?.();
        formik.resetForm({
            values: initialValues,
        });
    };

    const handleChangeAssignees = (name, value) => {
        const member = members.find(
            (member) =>
                (member.ldbId || member.guestId) === value ||
                (member.fullName?.toLocaleLowerCase() === value && member.isSynth)
        );
        const isSynth = member?.isSynth;

        const newAssignees = [
            {
                ...(isSynth
                    ? { kind: SpaceTaskAssignedToKindEnum.synth }
                    : {
                          kind: SpaceTaskAssignedToKindEnum.user,
                          ...(member?.isGuest ? { guestId: value } : { ldbId: value }),
                      }),
            },
        ];

        formik.setFieldValue("assignees", [...newAssignees]);
    };

    const endConditionOptions = [
        {
            label: "No end date",
            value: SpaceTaskEndConditionEnum.none,
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <p className="text-sm whitespace-nowrap">End after</p>
                    <TextInput
                        name={"endAfter"}
                        type={"number"}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "schedule.recurrence.endAfter")}
                        inputContainerClassname="!h-[24px] !w-[30px] !p-0"
                        inputPlaceholder={"End after"}
                        inputClassName=" !px-1 !py-0"
                    />

                    <p className="text-sm">Occurrences</p>
                </div>
            ),
            value: SpaceTaskEndConditionEnum.occurence,
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <p className="text-sm whitespace-nowrap">End by</p>

                    <TextInput
                        name={"endDate"}
                        type={"date"}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "schedule.recurrence.endAt")}
                        inputContainerClassname="!h-[24px] !w-[120px] !p-0"
                        inputClassName="!px-1 !py-0"
                        inputPlaceholder={"End date"}
                    />

                    <p className="text-sm">Date</p>
                </div>
            ),
            value: SpaceTaskEndConditionEnum.date,
        },
    ];

    return (
        <ModalContainer
            open={open}
            modalClassName={"py-10 px-10"}
            tailwindClassName="w-[500px] rounded-[8px]"
            showCloseIcon={false}
            closeModal={() => {
                handleAddEditSpaceCloseModal();
            }}
            {...restProps}
        >
            <form
                className="bg-white rounded-[8px] relative font-rocGroteskMedium"
                onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                }}
            >
                <div className="sticky top-0 bg-white rounded-t-lg z-[1200] px-6 font-rocGroteskMedium border-b border-b-slate-200 flex items-center justify-between py-3">
                    <p className="mt-1 text-base text-gm-50 ">Add a new task</p>
                    <i
                        className={`ri-close-fill text-lg text-gm-50 cursor-pointer`}
                        onClick={() => {
                            handleAddEditSpaceCloseModal();
                        }}
                    ></i>
                </div>
                <div className="px-6  pt-6 space-y-4 grid grid-cols-2 gap-3 overflow-y-auto !max-h-[70vh] pb-8 scrollbar-hidden">
                    <TextInput
                        name={"name"}
                        type={"text"}
                        placeholder={"Task name"}
                        disableNativeValidation
                        containerClassname="col-span-2"
                        {...getTextFieldProps(formik, "name")}
                    />
                    <TextareaInput
                        name={"description"}
                        rows={5}
                        placeholder={"Description"}
                        disableNativeValidation
                        containerClassname="col-span-2"
                        {...getTextFieldProps(formik, "description")}
                    />

                    <SelectInput
                        name="taskType"
                        placeholder={`Task type`}
                        dropdownOptions={Object.values(TaskScheduleTypeEnum).map((type) => ({
                            label: `${capitalizeFirstLetter(type?.replace("_", "-"))} task`,
                            value: type,
                        }))}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "schedule.type")}
                        handleChange={(name, value) => {
                            formik.setFieldValue("schedule.type", value);
                        }}
                    />

                    {formik.values.schedule.type === TaskScheduleTypeEnum.recurring && (
                        <SelectInput
                            name="frequency"
                            placeholder={`Frequency`}
                            dropdownOptions={Object.values(SpaceTaskFrequencyEnum).map((type) => ({
                                label: type?.replace("_", " "),
                                value: type,
                            }))}
                            disableNativeValidation
                            {...getTextFieldProps(formik, "schedule.recurrence.frequency")}
                        />
                    )}

                    {formik.values.schedule.type === TaskScheduleTypeEnum.oneTime && (
                        <SelectInput
                            name="status"
                            placeholder={`Status`}
                            dropdownOptions={Object.values(SpaceTaskStatusEnum).map((type) => ({
                                label: capitalizeFirstLetter(type?.replace("_", " ")),
                                value: type,
                            }))}
                            disableNativeValidation
                            {...getTextFieldProps(formik, "status")}
                            handleChange={(name, value) => {
                                formik.setFieldValue("status", value);
                            }}
                        />
                    )}

                    <TextInput
                        name={"startDate"}
                        type={"date"}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "schedule.startAt")}
                        placeholder={"Start date"}
                        value={formik.values.schedule.startAt}
                    />

                    {formik.values.schedule.type === TaskScheduleTypeEnum.oneTime && (
                        <TextInput
                            type={"date"}
                            disableNativeValidation
                            {...getTextFieldProps(formik, "schedule.endAt")}
                            value={formik.values.schedule.endAt}
                            placeholder={"End date"}
                        />
                    )}

                    {formik.values.schedule.type === TaskScheduleTypeEnum.recurring && (
                        <TextInput
                            disableNativeValidation
                            name={"time"}
                            type={"time"}
                            {...getTextFieldProps(formik, "schedule.time")}
                            placeholder={"Time"}
                            value={formik.values.schedule.time}
                        />
                    )}

                    {formik.values.schedule.type === TaskScheduleTypeEnum.recurring && (
                        <div className="space-y-4 col-span-2">
                            <div className="">
                                <p className="text-[#000] text-sm mb-4">End condition</p>
                                <div className="space-y-2">
                                    {endConditionOptions.map((option) => (
                                        <div
                                            className="flex items-center gap-2 text-slate-950"
                                            onClick={() =>
                                                formik.setFieldValue(
                                                    "schedule.recurrence.endCondition",
                                                    option.value
                                                )
                                            }
                                        >
                                            <i
                                                className={clsx(
                                                    `ri-radio-button-fill text-lg`,
                                                    formik.values.schedule.recurrence
                                                        .endCondition === option.value
                                                        ? "text-y-10"
                                                        : "text-slate-200"
                                                )}
                                            ></i>

                                            <p className="text-sm">{option.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <SelectInput
                        name="priority"
                        placeholder={`Priority`}
                        dropdownOptions={Object.values(SpaceTaskPriorityEnum).map((type) => ({
                            label: capitalizeFirstLetter(type?.replace("_", " ")),
                            value: type,
                        }))}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "priority")}
                        handleChange={(name, value) => {
                            formik.setFieldValue("priority", value);
                        }}
                    />

                    <SelectInput
                        name="assignedTo"
                        placeholder={`Assigned to`}
                        disableNativeValidation
                        {...getTextFieldProps(formik, "assignees")}
                        dropdownOptions={members?.map((member) => ({
                            label: member?.fullName ? member?.fullName : member?.email,
                            value: member?.isSynth ? "synth" : member.ldbId ?? member.guestId,
                            icon: (
                                <VendorAvatar
                                    size={30}
                                    imageSrc={member?.avatar}
                                    name={member?.fullName ? member?.fullName : member?.email}
                                />
                            ),
                        }))}
                        value={
                            formik.values.assignees?.length > 0
                                ? formik.values.assignees?.map(
                                      (assignee) =>
                                          (assignee as any).ldbId ??
                                          (assignee as any).guestId ??
                                          "synth"
                                  )?.[0]
                                : ""
                        }
                        handleChange={handleChangeAssignees}
                    />

                    <div className="space-y-2">
                        <p className="text-[#000] text-sm">How should I remind you?</p>
                        <div className="flex items-center gap-4">
                            {Object.values(SpaceTaskReminderMethodEnum).map((method) => (
                                <Checkbox
                                    checked={formik.values.reminderMethods.includes(method)}
                                    onClick={(e) => {
                                        const newReminderMethods = [
                                            ...formik.values.reminderMethods,
                                        ];

                                        if (formik.values.reminderMethods.includes(method)) {
                                            newReminderMethods.splice(
                                                newReminderMethods.indexOf(method),
                                                1
                                            );
                                        } else {
                                            newReminderMethods.push(method);
                                        }

                                        formik.setFieldValue("reminderMethods", [
                                            ...newReminderMethods,
                                        ]);
                                    }}
                                    label={
                                        <p className="text-[#64748B] font-rocGroteskMedium  text-xs">
                                            {capitalizeFirstLetter(method?.split("_").join(" "))}
                                        </p>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-t-slate-200 py-4 px-6 ">
                    <Button
                        btnText={"Cancel"}
                        type="button"
                        btnClassname="!w-fit !bg-n-20 !text-gm-50"
                        onClick={() => {
                            handleAddEditSpaceCloseModal();
                        }}
                    />

                    <Button
                        isLoading={createSpaceTaskMutationResult.isLoading}
                        btnText={"Add new task"}
                        type="submit"
                        btnClassname="!w-fit"
                    />
                </div>
            </form>
        </ModalContainer>
    );
};

export default SpaceTaskAddModal;

// Function to merge date and time using moment.js
const mergeDateTime = (date: string, time: string): Date => {
    if (!date || !time) return new Date(date || time);

    const mergedDateTime = moment(date).format("YYYY-MM-DD") + " " + time;
    return moment(mergedDateTime).toDate();
};
